import { useState } from 'react';
import { ChevronDown, ChevronRight, Plus, Search } from 'lucide-react';
import { Combobox } from '@/components/ui/combobox';
import type { BreakdownTemplate, CommonTemplateItem } from '@jobos/shared';

export interface TemplateItem {
  id: string;
  category: string;
  description: string;
  defaultCost?: number;
  isReusable?: boolean;
}

interface TemplateLineItemsProps {
  onAddItem: (item: TemplateItem) => void;
  usedTemplateIds?: Set<string>;
  commonItems?: CommonTemplateItem[];
  /** Full job type templates, already filtered to active ones. */
  fullTemplates?: BreakdownTemplate[];
  onApplyFullTemplate?: (template: BreakdownTemplate) => void;
  /** The job's own project type — preselected in the type picker when it has templates. */
  jobProjectType?: string | null;
}

export function TemplateLineItems({
  onAddItem,
  usedTemplateIds = new Set(),
  commonItems = [],
  fullTemplates = [],
  onApplyFullTemplate,
  jobProjectType,
}: TemplateLineItemsProps) {
  const [isFullTemplatesExpanded, setIsFullTemplatesExpanded] = useState(false);
  // null = "not chosen yet", which falls back to the job's own project type.
  // Keeping the fallback in render (rather than an effect) means the default
  // still lands once the templates query resolves.
  const [selectedProjectType, setSelectedProjectType] = useState<string | null>(null);
  const [isLineItemsExpanded, setIsLineItemsExpanded] = useState(false);
  const [isReusableExpanded, setIsReusableExpanded] = useState(false);
  const [isDisclaimersExpanded, setIsDisclaimersExpanded] = useState(false);
  const [lineItemsFilter, setLineItemsFilter] = useState('');

  // Split items into three groups
  const commonLineItems = commonItems.filter(item => !item.isDisclaimer && !item.isReusable);
  const reusableLineItems = commonItems.filter(item => !item.isDisclaimer && item.isReusable);
  const disclaimerItems = commonItems.filter(item => item.isDisclaimer);

  // Filter out templates that have already been added (non-reusable only)
  const availableLineItems = commonLineItems.filter(item => !usedTemplateIds.has(item.id));
  const availableDisclaimers = disclaimerItems.filter(item => !usedTemplateIds.has(item.id));
  // Reusable items are always available
  const availableReusable = reusableLineItems;

  // Apply text filter to line items
  const filteredLineItems = availableLineItems.filter(item => {
    if (!lineItemsFilter.trim()) return true;
    const searchTerm = lineItemsFilter.toLowerCase();
    return item.category.toLowerCase().includes(searchTerm) ||
           (item.description ?? '').toLowerCase().includes(searchTerm);
  });

  // Full job type templates are picked in two steps — a project type, then a
  // template of that type. A flat list of every template gets overwhelming fast.
  // Only types that actually have templates are offered.
  const projectTypesWithTemplates = Array.from(
    new Set(fullTemplates.map((t) => t.projectType)),
  ).sort((a, b) => a.localeCompare(b));
  const effectiveProjectType =
    selectedProjectType ??
    (jobProjectType && projectTypesWithTemplates.includes(jobProjectType) ? jobProjectType : null);
  const templatesForType = effectiveProjectType
    ? fullTemplates.filter((t) => t.projectType === effectiveProjectType)
    : [];

  const showFullTemplates = fullTemplates.length > 0 && !!onApplyFullTemplate;

  const toTemplateItem = (item: CommonTemplateItem): TemplateItem => ({
    id: item.id,
    category: item.category,
    description: item.description ?? '',
    isReusable: item.isReusable,
  });

  const renderTemplateItem = (item: CommonTemplateItem) => {
    return (
      <div
        key={item.id}
        onClick={() => onAddItem(toTemplateItem(item))}
        className="group flex items-start gap-2 px-3 py-2 rounded-md border transition-colors border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/50 cursor-pointer"
      >
        <div className="flex-1 min-w-0">
          <span className={`inline-block text-xs px-2 py-0.5 rounded-full border ${
            item.isDisclaimer
              ? 'bg-amber-50 text-amber-700 border-amber-200'
              : 'bg-white text-gray-700 border-gray-200'
          }`}>
            {item.category}
          </span>
          <p className="text-gray-600 text-sm mt-1">{item.description}</p>
        </div>
        <button
          className="text-gray-400 group-hover:text-blue-600 transition-colors mt-1"
          aria-label="Add to line items"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
    );
  };

  // Hide the entire component if all templates have been used and no reusable items
  if (
    availableLineItems.length === 0 &&
    availableReusable.length === 0 &&
    availableDisclaimers.length === 0 &&
    !showFullTemplates
  ) {
    return null;
  }

  return (
    <div className="p-3">
      {/* Common Line Items Section */}
      {availableLineItems.length > 0 && (
        <div className="mb-4">
          <button
            onClick={() => setIsLineItemsExpanded(!isLineItemsExpanded)}
            className="flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-gray-900 mb-2"
          >
            {isLineItemsExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            Common Line Items
          </button>
          {isLineItemsExpanded && (
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Filter by category or description..."
                  value={lineItemsFilter}
                  onChange={(e) => setLineItemsFilter(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              {filteredLineItems.length > 0 ? (
                filteredLineItems.map(renderTemplateItem)
              ) : (
                <p className="text-sm text-gray-500 py-2 text-center">No matching line items</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Reusable Line Items Section */}
      {availableReusable.length > 0 && (
        <div className="mb-4">
          <button
            onClick={() => setIsReusableExpanded(!isReusableExpanded)}
            className="flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-gray-900 mb-2"
          >
            {isReusableExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            Reusable Line Items
          </button>
          {isReusableExpanded && (
            <div className="space-y-2">
              {availableReusable.map(renderTemplateItem)}
            </div>
          )}
        </div>
      )}

      {/* Disclaimers Section */}
      {availableDisclaimers.length > 0 && (
        <div className="mb-4">
          <button
            onClick={() => setIsDisclaimersExpanded(!isDisclaimersExpanded)}
            className="flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-gray-900 mb-2"
          >
            {isDisclaimersExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            Disclaimers
          </button>
          {isDisclaimersExpanded && (
            <div className="space-y-2">
              {availableDisclaimers.map(renderTemplateItem)}
            </div>
          )}
        </div>
      )}

      {/* Job Type Templates Section — whole-breakdown templates, not single line items */}
      {showFullTemplates && (
        <div>
          <button
            onClick={() => setIsFullTemplatesExpanded(!isFullTemplatesExpanded)}
            className="flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-gray-900 mb-2"
          >
            {isFullTemplatesExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            Job Type Templates
          </button>
          {isFullTemplatesExpanded && (
            <div className="space-y-2">
              <Combobox
                value={effectiveProjectType ?? ''}
                onValueChange={(value) => setSelectedProjectType(value as string)}
                options={projectTypesWithTemplates.map((t) => ({ value: t, label: t }))}
                placeholder="Select a job type"
                searchPlaceholder="Search job types..."
                emptyText="No job types with templates."
              />
              {!effectiveProjectType ? (
                <p className="text-sm text-gray-500 py-2 text-center">
                  Pick a job type to see its templates
                </p>
              ) : templatesForType.length === 0 ? (
                <p className="text-sm text-gray-500 py-2 text-center">
                  No templates for this job type
                </p>
              ) : (
                <div className="space-y-1">
                  {templatesForType.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => onApplyFullTemplate?.(t)}
                      className="flex w-full items-center justify-between gap-2 px-3 py-2 rounded-md border border-gray-200 bg-white text-left hover:border-blue-300 hover:bg-blue-50/50 transition-colors"
                    >
                      <span className="text-sm text-gray-700 truncate">{t.name}</span>
                      {t.isDefault && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                          Default
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
